<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import addDays from 'date-fns/addDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import fromUnixTime from 'date-fns/fromUnixTime';
import { useStore } from 'dashboard/composables/store';
import subDays from 'date-fns/subDays';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import { DATE_RANGE_OPTIONS, DATE_RANGE_ORDER } from '../constants';

const props = defineProps({
  showAddFilter: {
    type: Boolean,
    default: true,
  },
  showDateFilter: {
    type: Boolean,
    default: true,
  },
  emitInitialChange: {
    type: Boolean,
    default: true,
  },
  initialDateRangeId: {
    type: String,
    default: DATE_RANGE_OPTIONS.LAST_7_DAYS.id,
  },
  allowedDateRangeIds: {
    type: Array,
    default: () => [],
  },
  showInbox: {
    type: Boolean,
    default: true,
  },
  showAgents: {
    type: Boolean,
    default: false,
  },
  showLabels: {
    type: Boolean,
    default: false,
  },
  showTeams: {
    type: Boolean,
    default: false,
  },
  showVip: {
    type: Boolean,
    default: false,
  },
  showIssue: {
    type: Boolean,
    default: false,
  },
  showPriority: {
    type: Boolean,
    default: false,
  },
  livechatOnlyInboxes: {
    type: Boolean,
    default: false,
  },
  multiInboxMode: {
    type: Boolean,
    default: false,
  },
  forcedInboxId: {
    type: Number,
    default: undefined,
  },
  forcedInbox: {
    type: Object,
    default: undefined,
  },
  forcedInboxes: {
    type: Array,
    default: undefined,
  },
  forcedAgents: {
    type: Array,
    default: undefined,
  },
  forcedLabel: {
    type: Object,
    default: undefined,
  },
  forcedTeam: {
    type: Object,
    default: undefined,
  },
  forcedVip: {
    type: Object,
    default: undefined,
  },
  forcedIssue: {
    type: Object,
    default: undefined,
  },
  forcedPriority: {
    type: Object,
    default: undefined,
  },
  forcedFrom: {
    type: Number,
    default: null,
  },
  forcedTo: {
    type: Number,
    default: null,
  },
  hideInactiveFilters: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['filter-change']);

const { t } = useI18n();

const store = useStore();

const showDateMenu = ref(false);
const showInboxMenu = ref(false);
const showAgentsMenu = ref(false);
const showLabelsMenu = ref(false);
const showTeamsMenu = ref(false);
const showVipMenu = ref(false);
const showIssueMenu = ref(false);
const showPriorityMenu = ref(false);
const showAddFilterMenu = ref(false);
const dateMenuRef = ref(null);
const inboxMenuRef = ref(null);
const agentsMenuRef = ref(null);
const labelsMenuRef = ref(null);
const teamsMenuRef = ref(null);
const vipMenuRef = ref(null);
const issueMenuRef = ref(null);
const priorityMenuRef = ref(null);
const addFilterMenuRef = ref(null);
const customDatePickerRef = ref(null);

const selectedDateRangeId = ref(props.initialDateRangeId);
const customDateRange = ref([new Date(), new Date()]);
const selectedInbox = ref(null);
const selectedInboxes = ref([]);
const selectedAgents = ref([]);
const selectedLabel = ref(null);
const selectedTeam = ref(null);
const selectedVip = ref(null);
const selectedIssue = ref(null);
const selectedPriority = ref(null);

const enabledFilterKeys = ref([]);

const inboxes = computed(() => {
  if (props.livechatOnlyInboxes) {
    return store.getters['inboxes/getWebsiteInboxes'] || [];
  }
  return store.getters['inboxes/getInboxes'] || [];
});

const agents = computed(() => store.getters['agents/getAgents'] || []);
const labels = computed(() => store.getters['labels/getLabels'] || []);
const teams = computed(() => store.getters['teams/getTeams'] || []);
const issues = computed(() => store.getters['issues/getIssues'] || []);
const vipLevels = computed(() => {
  const activeLevels = store.getters['vipLevels/getActiveVipLevels'] || [];
  if (activeLevels.length) return activeLevels;
  return store.getters['vipLevels/getVipLevels'] || [];
});

const priorityOptions = computed(() => [
  { id: 'low', label: t('REPORT.COMPACT_FILTERS.PRIORITY_OPTIONS.LOW') },
  { id: 'medium', label: t('REPORT.COMPACT_FILTERS.PRIORITY_OPTIONS.MEDIUM') },
  { id: 'high', label: t('REPORT.COMPACT_FILTERS.PRIORITY_OPTIONS.HIGH') },
  { id: 'urgent', label: t('REPORT.COMPACT_FILTERS.PRIORITY_OPTIONS.URGENT') },
]);

const normalizePositiveId = value => {
  const normalizedId = Number(value || 0);
  return Number.isFinite(normalizedId) && normalizedId > 0
    ? normalizedId
    : null;
};

const sameSelectionId = (left, right) =>
  normalizePositiveId(left?.id) === normalizePositiveId(right?.id);

const sameAgentSelection = (left, right) => {
  if (left.length !== right.length) return false;
  return left.every(
    (agent, index) =>
      normalizePositiveId(agent?.id) === normalizePositiveId(right[index]?.id)
  );
};

const dateOptions = computed(() => {
  const allowedRangeIds = Array.isArray(props.allowedDateRangeIds)
    ? props.allowedDateRangeIds
    : [];

  return DATE_RANGE_ORDER.map(id => DATE_RANGE_OPTIONS[id])
    .filter(Boolean)
    .filter(option => {
      if (!allowedRangeIds.length) return true;
      return allowedRangeIds.includes(option.id);
    })
    .map(option => ({
      ...option,
      name: t(option.translationKey),
    }));
});

const selectedDateRange = computed(() => {
  return (
    dateOptions.value.find(option => option.id === selectedDateRangeId.value) ||
    dateOptions.value[0]
  );
});

const isCustomDateRange = computed(
  () => selectedDateRange.value?.id === DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id
);

const to = computed(() => {
  if (isCustomDateRange.value) {
    return getUnixEndOfDay(customDateRange.value[1]);
  }
  const endOffset =
    selectedDateRange.value?.endOffset ?? selectedDateRange.value?.offset ?? 0;
  return getUnixEndOfDay(subDays(new Date(), endOffset));
});

const from = computed(() => {
  if (isCustomDateRange.value) {
    return getUnixStartOfDay(customDateRange.value[0]);
  }
  const startOffset =
    selectedDateRange.value?.startOffset ??
    selectedDateRange.value?.offset ??
    0;
  return getUnixStartOfDay(subDays(new Date(), startOffset));
});

const dateChipLabel = computed(() => {
  if (
    isCustomDateRange.value &&
    isValidCustomDateRange(customDateRange.value)
  ) {
    return `${customDateRange.value[0].toLocaleDateString()} - ${customDateRange.value[1].toLocaleDateString()}`;
  }
  return selectedDateRange.value?.name || t('REPORT.DATE_RANGE_OPTIONS.TODAY');
});

const hasInboxSelection = computed(() =>
  props.multiInboxMode
    ? selectedInboxes.value.length > 0
    : Boolean(selectedInbox.value?.id)
);
const hasAgentSelection = computed(() => selectedAgents.value.length > 0);
const hasLabelSelection = computed(() => Boolean(selectedLabel.value?.id));
const hasTeamSelection = computed(() => Boolean(selectedTeam.value?.id));
const hasVipSelection = computed(() => Boolean(selectedVip.value?.id));
const hasIssueSelection = computed(() => Boolean(selectedIssue.value?.id));
const hasPrioritySelection = computed(() =>
  Boolean(selectedPriority.value?.id)
);

const isFilterKeyEnabled = key => enabledFilterKeys.value.includes(key);

const isFilterKeyVisible = key => {
  if (!props.hideInactiveFilters) return true;
  if (isFilterKeyEnabled(key)) return true;

  const selectionMap = {
    inbox: hasInboxSelection.value,
    agents: hasAgentSelection.value,
    labels: hasLabelSelection.value,
    teams: hasTeamSelection.value,
    vip: hasVipSelection.value,
    issue: hasIssueSelection.value,
    priority: hasPrioritySelection.value,
  };

  return Boolean(selectionMap[key]);
};

const enableFilterKey = key => {
  if (isFilterKeyEnabled(key)) return;
  enabledFilterKeys.value = [...enabledFilterKeys.value, key];
};

const disableFilterKey = key => {
  enabledFilterKeys.value = enabledFilterKeys.value.filter(
    item => item !== key
  );
};

const activateFilterKey = key => {
  enableFilterKey(key);
  showAddFilterMenu.value = false;

  const menuMap = {
    inbox: 'inbox',
    agents: 'agents',
    labels: 'labels',
    teams: 'teams',
    vip: 'vip',
    issue: 'issue',
    priority: 'priority',
  };

  const target = menuMap[key] || null;
  closeAllMenus(target);
};

const deactivateFilterKey = key => {
  disableFilterKey(key);

  if (key === 'inbox') onClearInbox();
  if (key === 'agents') clearAgents();
  if (key === 'labels') clearLabel();
  if (key === 'teams') onClearTeam();
  if (key === 'vip') onClearVip();
  if (key === 'issue') onClearIssue();
  if (key === 'priority') onClearPriority();
};

const onAddFilterItemClick = key => {
  const supported = [
    'agents',
    'inbox',
    'labels',
    'teams',
    'vip',
    'issue',
    'priority',
  ];
  if (!supported.includes(key)) return;

  if (isFilterKeyEnabled(key)) {
    showAddFilterMenu.value = false;
    closeAllMenus();
    deactivateFilterKey(key);
    return;
  }

  activateFilterKey(key);
};

const rangeDayCount = computed(() => {
  const start = fromUnixTime(from.value);
  const end = fromUnixTime(to.value);
  const days = differenceInCalendarDays(end, start) + 1;
  return Math.max(1, days);
});

const canShiftNext = computed(() => {
  const todayEnd = getUnixEndOfDay(new Date());
  const nextEnd = addDays(fromUnixTime(to.value), rangeDayCount.value);
  return getUnixEndOfDay(nextEnd) <= todayEnd;
});

const shiftDateRange = direction => {
  const dir = direction === 'next' ? 'next' : 'prev';
  if (dir === 'next' && !canShiftNext.value) return;

  const delta = dir === 'next' ? rangeDayCount.value : -rangeDayCount.value;

  selectedDateRangeId.value = DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id;
  customDateRange.value = [
    addDays(fromUnixTime(from.value), delta),
    addDays(fromUnixTime(to.value), delta),
  ];
  showDateMenu.value = false;
};

const allInboxesLabel = computed(() => t('REPORT.COMPACT_FILTERS.ALL_INBOXES'));
const allAgentsLabel = computed(() => t('REPORT.COMPACT_FILTERS.ALL_AGENTS'));
const allLabelsLabel = computed(() => t('REPORT.COMPACT_FILTERS.ALL_LABELS'));
const allTeamsLabel = computed(() => t('REPORT.COMPACT_FILTERS.ALL_TEAMS'));
const allVipLabel = computed(() => t('REPORT.COMPACT_FILTERS.ALL_VIP'));
const allIssueLabel = computed(() => t('REPORT.COMPACT_FILTERS.ALL_ISSUES'));
const allPriorityLabel = computed(() =>
  t('REPORT.COMPACT_FILTERS.ALL_PRIORITIES')
);
const inboxChipLabel = computed(() => {
  if (props.multiInboxMode) {
    const count = selectedInboxes.value.length;
    if (!count) return allInboxesLabel.value;
    if (count === 1)
      return selectedInboxes.value[0]?.name || allInboxesLabel.value;
    return t('REPORT.COMPACT_FILTERS.INBOXES_COUNT', { count });
  }
  return selectedInbox.value?.name || allInboxesLabel.value;
});

const agentsChipLabel = computed(() => {
  const count = selectedAgents.value.length;
  if (!count) return allAgentsLabel.value;
  if (count === 1) return selectedAgents.value[0]?.name || allAgentsLabel.value;
  return t('REPORT.COMPACT_FILTERS.AGENTS_COUNT', { count });
});

const labelsChipLabel = computed(() => {
  if (!selectedLabel.value) return allLabelsLabel.value;
  return selectedLabel.value?.title || allLabelsLabel.value;
});

const teamsChipLabel = computed(() => {
  if (!selectedTeam.value) return allTeamsLabel.value;
  return selectedTeam.value?.name || allTeamsLabel.value;
});

const vipChipLabel = computed(() => {
  if (!selectedVip.value) return allVipLabel.value;
  return (
    selectedVip.value?.name ||
    `${t('REPORT.COMPACT_FILTERS.VIP')} ${selectedVip.value?.level || ''}`.trim()
  );
});

const issueChipLabel = computed(() => {
  if (!selectedIssue.value) return allIssueLabel.value;
  return selectedIssue.value?.name || allIssueLabel.value;
});

const priorityChipLabel = computed(() => {
  if (!selectedPriority.value?.id) return allPriorityLabel.value;
  return selectedPriority.value?.label || allPriorityLabel.value;
});

const vipOptionLabel = vipLevel => {
  if (!vipLevel) return '';
  return (
    vipLevel.name ||
    `${t('REPORT.COMPACT_FILTERS.VIP')} ${vipLevel.level ?? vipLevel.id ?? ''}`.trim()
  );
};

const emitChange = () => {
  emit('filter-change', {
    from: from.value,
    to: to.value,
    selectedInbox: props.multiInboxMode ? null : selectedInbox.value,
    selectedInboxes: selectedInboxes.value,
    selectedAgents: selectedAgents.value,
    selectedLabel: selectedLabel.value,
    selectedTeam: selectedTeam.value,
    selectedVip: selectedVip.value,
    selectedIssue: selectedIssue.value,
    selectedPriority: selectedPriority.value,
  });
};

const closeAllMenus = (except = null) => {
  showDateMenu.value = except === 'date';
  showInboxMenu.value = except === 'inbox';
  showAgentsMenu.value = except === 'agents';
  showLabelsMenu.value = except === 'labels';
  showTeamsMenu.value = except === 'teams';
  showVipMenu.value = except === 'vip';
  showIssueMenu.value = except === 'issue';
  showPriorityMenu.value = except === 'priority';
  showAddFilterMenu.value = except === 'addFilter';
};

const toggleMenu = menu => {
  const isOpen = {
    date: showDateMenu.value,
    inbox: showInboxMenu.value,
    agents: showAgentsMenu.value,
    labels: showLabelsMenu.value,
    teams: showTeamsMenu.value,
    vip: showVipMenu.value,
    issue: showIssueMenu.value,
    priority: showPriorityMenu.value,
    addFilter: showAddFilterMenu.value,
  }[menu];

  closeAllMenus(isOpen ? null : menu);
};

const isDatePickerPopupClick = target =>
  target instanceof Element && Boolean(target.closest('.mx-datepicker-popup'));

const isValidCustomDateRange = value =>
  Array.isArray(value) &&
  value.length === 2 &&
  value[0] instanceof Date &&
  !Number.isNaN(value[0].getTime()) &&
  value[1] instanceof Date &&
  !Number.isNaN(value[1].getTime());

const isValidUnixRange = (nextFrom, nextTo) =>
  Number.isFinite(nextFrom) &&
  Number.isFinite(nextTo) &&
  nextFrom > 0 &&
  nextTo > 0 &&
  nextFrom <= nextTo;

const datePickerLang = {
  formatLocale: {
    firstDayOfWeek: 1,
    weekdaysMin: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
  },
  yearFormat: 'YYYY',
  monthFormat: 'MMMM',
  monthBeforeYear: false,
};

const disableFutureDates = date => date > new Date();

const openCustomDatePicker = () => {
  // Try the vue2-datepicker internal openPopup method first (most reliable)
  const pickerVm = customDatePickerRef.value;
  if (pickerVm?.openPopup) {
    pickerVm.openPopup();
    return;
  }

  // Fallback: simulate user interaction on the hidden input element
  const rootEl = pickerVm?.$el;
  if (!(rootEl instanceof HTMLElement)) return;

  const inputEl = rootEl.querySelector('.mx-input, input');
  if (!(inputEl instanceof HTMLElement)) return;

  inputEl.dispatchEvent(
    new MouseEvent('mousedown', { bubbles: true, cancelable: true })
  );
  inputEl.dispatchEvent(
    new MouseEvent('click', { bubbles: true, cancelable: true })
  );
};

const onSelectDate = option => {
  selectedDateRangeId.value = option.id;
  if (option.id === DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id) {
    // Close the dropdown list first, then open the date picker popup directly
    showDateMenu.value = false;
    nextTick(() => openCustomDatePicker());
    return;
  }
  showDateMenu.value = false;
  emitChange();
};

const onSelectInbox = inbox => {
  selectedInbox.value = inbox;
  showInboxMenu.value = false;
  emitChange();
};

const onClearInbox = () => {
  selectedInbox.value = null;
  selectedInboxes.value = [];
  showInboxMenu.value = false;
  emitChange();
};

const toggleInbox = inbox => {
  const id = inbox?.id;
  if (!id) return;
  const idx = selectedInboxes.value.findIndex(item => item.id === id);
  if (idx >= 0) {
    selectedInboxes.value = selectedInboxes.value.filter(
      item => item.id !== id
    );
  } else {
    selectedInboxes.value = [...selectedInboxes.value, inbox];
  }
  emitChange();
};

const toggleAgent = agent => {
  const id = agent?.id;
  if (!id) return;
  const idx = selectedAgents.value.findIndex(item => item.id === id);
  if (idx >= 0) {
    selectedAgents.value = selectedAgents.value.filter(item => item.id !== id);
  } else {
    selectedAgents.value = [...selectedAgents.value, agent];
  }
  emitChange();
};

const clearAgents = () => {
  selectedAgents.value = [];
  showAgentsMenu.value = false;
  emitChange();
};

const onSelectLabel = label => {
  selectedLabel.value = label;
  showLabelsMenu.value = false;
  emitChange();
};

const clearLabel = () => {
  selectedLabel.value = null;
  showLabelsMenu.value = false;
  emitChange();
};

const onSelectTeam = team => {
  selectedTeam.value = team;
  showTeamsMenu.value = false;
  emitChange();
};

const onClearTeam = () => {
  selectedTeam.value = null;
  showTeamsMenu.value = false;
  emitChange();
};

const onSelectVip = vipLevel => {
  selectedVip.value = vipLevel;
  showVipMenu.value = false;
  emitChange();
};

const onClearVip = () => {
  selectedVip.value = null;
  showVipMenu.value = false;
  emitChange();
};

const onSelectIssue = issue => {
  selectedIssue.value = issue;
  showIssueMenu.value = false;
  emitChange();
};

const onClearIssue = () => {
  selectedIssue.value = null;
  showIssueMenu.value = false;
  emitChange();
};

const onSelectPriority = option => {
  selectedPriority.value = option;
  showPriorityMenu.value = false;
  emitChange();
};

const onClearPriority = () => {
  selectedPriority.value = null;
  showPriorityMenu.value = false;
  emitChange();
};

const handleDocumentClick = event => {
  const target = event.target;
  if (
    showDateMenu.value &&
    !isDatePickerPopupClick(target) &&
    dateMenuRef.value &&
    !dateMenuRef.value.contains(target)
  ) {
    showDateMenu.value = false;
  }
  if (
    showInboxMenu.value &&
    inboxMenuRef.value &&
    !inboxMenuRef.value.contains(target)
  ) {
    showInboxMenu.value = false;
  }
  if (
    showAgentsMenu.value &&
    agentsMenuRef.value &&
    !agentsMenuRef.value.contains(target)
  ) {
    showAgentsMenu.value = false;
  }
  if (
    showLabelsMenu.value &&
    labelsMenuRef.value &&
    !labelsMenuRef.value.contains(target)
  ) {
    showLabelsMenu.value = false;
  }
  if (
    showTeamsMenu.value &&
    teamsMenuRef.value &&
    !teamsMenuRef.value.contains(target)
  ) {
    showTeamsMenu.value = false;
  }
  if (
    showVipMenu.value &&
    vipMenuRef.value &&
    !vipMenuRef.value.contains(target)
  ) {
    showVipMenu.value = false;
  }
  if (
    showIssueMenu.value &&
    issueMenuRef.value &&
    !issueMenuRef.value.contains(target)
  ) {
    showIssueMenu.value = false;
  }
  if (
    showPriorityMenu.value &&
    priorityMenuRef.value &&
    !priorityMenuRef.value.contains(target)
  ) {
    showPriorityMenu.value = false;
  }
  if (
    showAddFilterMenu.value &&
    addFilterMenuRef.value &&
    !addFilterMenuRef.value.contains(target)
  ) {
    showAddFilterMenu.value = false;
  }
};

const handleEscape = event => {
  if (event.key !== 'Escape') return;
  closeAllMenus();
};

onMounted(() => {
  store.dispatch('inboxes/get');
  if (props.showAgents) store.dispatch('agents/get');
  if (props.showLabels) store.dispatch('labels/get');
  if (props.showTeams) store.dispatch('teams/get');
  if (props.showIssue) store.dispatch('issues/get');
  if (props.showVip) store.dispatch('vipLevels/get');
  if (props.emitInitialChange) {
    emitChange();
  }
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener('keydown', handleEscape);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
  document.removeEventListener('keydown', handleEscape);
});

watch(customDateRange, () => {
  if (
    isCustomDateRange.value &&
    isValidCustomDateRange(customDateRange.value)
  ) {
    emitChange();
  }
});

watch(
  [() => props.forcedFrom, () => props.forcedTo],
  ([nextFrom, nextTo]) => {
    if (!isValidUnixRange(nextFrom, nextTo)) return;
    if (from.value === nextFrom && to.value === nextTo) return;
    selectedDateRangeId.value = DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id;
    customDateRange.value = [fromUnixTime(nextFrom), fromUnixTime(nextTo)];
    showDateMenu.value = false;
  },
  { immediate: true }
);

watch(
  [() => props.forcedInbox, () => props.forcedInboxId, inboxes],
  ([nextInbox, nextInboxId, availableInboxes]) => {
    if (nextInbox === undefined && nextInboxId === undefined) return;

    if (nextInbox === null || nextInboxId === null) {
      if (!selectedInbox.value) return;
      selectedInbox.value = null;
      showInboxMenu.value = false;
      return;
    }

    const normalizedInboxId = normalizePositiveId(nextInbox?.id || nextInboxId);
    if (!Number.isFinite(normalizedInboxId) || normalizedInboxId <= 0) return;

    const matchedInbox = (Array.isArray(availableInboxes)
      ? availableInboxes
      : []
    ).find(inbox => inbox.id === normalizedInboxId) || {
      id: normalizedInboxId,
      name: nextInbox?.name,
    };

    if (
      selectedInbox.value?.id === normalizedInboxId &&
      selectedInbox.value?.name === matchedInbox?.name
    ) {
      return;
    }

    selectedInbox.value = matchedInbox;
    showInboxMenu.value = false;
  },
  { immediate: true }
);

watch(
  [() => props.forcedInboxes, inboxes],
  ([nextInboxes, availableInboxes]) => {
    if (!props.multiInboxMode) return;
    if (nextInboxes === undefined) return;

    if (!Array.isArray(nextInboxes) || !nextInboxes.length) {
      if (!selectedInboxes.value.length) return;
      selectedInboxes.value = [];
      showInboxMenu.value = false;
      return;
    }

    const matchedInboxes = nextInboxes
      .map(inbox => {
        const normalizedId = normalizePositiveId(inbox?.id);
        if (!normalizedId) return null;
        return (
          (Array.isArray(availableInboxes) ? availableInboxes : []).find(
            item => item.id === normalizedId
          ) || { id: normalizedId, name: inbox?.name }
        );
      })
      .filter(Boolean)
      .sort((left, right) => left.id - right.id);

    if (
      selectedInboxes.value.length === matchedInboxes.length &&
      selectedInboxes.value.every((item, i) => item.id === matchedInboxes[i].id)
    )
      return;

    selectedInboxes.value = matchedInboxes;
    showInboxMenu.value = false;
  },
  { immediate: true }
);

watch(
  [() => props.forcedAgents, agents],
  ([nextAgents, availableAgents]) => {
    if (nextAgents === undefined) return;

    if (!Array.isArray(nextAgents) || !nextAgents.length) {
      if (!selectedAgents.value.length) return;
      selectedAgents.value = [];
      showAgentsMenu.value = false;
      return;
    }

    const matchedAgents = nextAgents
      .map(agent => {
        const normalizedAgentId = normalizePositiveId(agent?.id);
        if (!normalizedAgentId) return null;

        return (
          (Array.isArray(availableAgents) ? availableAgents : []).find(
            item => item.id === normalizedAgentId
          ) || {
            id: normalizedAgentId,
            name: agent?.name,
            email: agent?.email,
          }
        );
      })
      .filter(Boolean)
      .sort((left, right) => left.id - right.id);

    if (sameAgentSelection(selectedAgents.value, matchedAgents)) return;
    selectedAgents.value = matchedAgents;
    showAgentsMenu.value = false;
  },
  { immediate: true }
);

watch(
  [() => props.forcedLabel, labels],
  ([nextLabel, availableLabels]) => {
    if (nextLabel === undefined) return;

    if (nextLabel === null) {
      if (!selectedLabel.value) return;
      selectedLabel.value = null;
      showLabelsMenu.value = false;
      return;
    }

    const normalizedLabelId = normalizePositiveId(nextLabel?.id);
    if (!normalizedLabelId) return;

    const matchedLabel = (Array.isArray(availableLabels)
      ? availableLabels
      : []
    ).find(label => label.id === normalizedLabelId) || {
      id: normalizedLabelId,
      title: nextLabel?.title,
    };

    if (sameSelectionId(selectedLabel.value, matchedLabel)) return;
    selectedLabel.value = matchedLabel;
    showLabelsMenu.value = false;
  },
  { immediate: true }
);

watch(
  [() => props.forcedTeam, teams],
  ([nextTeam, availableTeams]) => {
    if (nextTeam === undefined) return;

    if (nextTeam === null) {
      if (!selectedTeam.value) return;
      selectedTeam.value = null;
      showTeamsMenu.value = false;
      return;
    }

    const normalizedTeamId = normalizePositiveId(nextTeam?.id);
    if (!normalizedTeamId) return;

    const matchedTeam = (Array.isArray(availableTeams)
      ? availableTeams
      : []
    ).find(team => team.id === normalizedTeamId) || {
      id: normalizedTeamId,
      name: nextTeam?.name,
    };

    if (sameSelectionId(selectedTeam.value, matchedTeam)) return;
    selectedTeam.value = matchedTeam;
    showTeamsMenu.value = false;
  },
  { immediate: true }
);

watch(
  [() => props.forcedVip, vipLevels],
  ([nextVip, availableVipLevels]) => {
    if (nextVip === undefined) return;

    if (nextVip === null) {
      if (!selectedVip.value) return;
      selectedVip.value = null;
      showVipMenu.value = false;
      return;
    }

    const normalizedVipId = normalizePositiveId(nextVip?.id);
    if (!normalizedVipId) return;

    const matchedVip = (Array.isArray(availableVipLevels)
      ? availableVipLevels
      : []
    ).find(vip => vip.id === normalizedVipId) || {
      id: normalizedVipId,
      name: nextVip?.name,
      level: nextVip?.level,
    };

    if (sameSelectionId(selectedVip.value, matchedVip)) return;
    selectedVip.value = matchedVip;
    showVipMenu.value = false;
  },
  { immediate: true }
);

watch(
  [() => props.forcedIssue, issues],
  ([nextIssue, availableIssues]) => {
    if (nextIssue === undefined) return;

    if (nextIssue === null) {
      if (!selectedIssue.value) return;
      selectedIssue.value = null;
      showIssueMenu.value = false;
      return;
    }

    const normalizedIssueId = normalizePositiveId(nextIssue?.id);
    if (!normalizedIssueId) return;

    const matchedIssue = (Array.isArray(availableIssues)
      ? availableIssues
      : []
    ).find(issue => issue.id === normalizedIssueId) || {
      id: normalizedIssueId,
      name: nextIssue?.name,
    };

    if (sameSelectionId(selectedIssue.value, matchedIssue)) return;
    selectedIssue.value = matchedIssue;
    showIssueMenu.value = false;
  },
  { immediate: true }
);

watch(
  () => props.forcedPriority,
  nextPriority => {
    if (nextPriority === undefined) return;

    if (nextPriority === null) {
      if (!selectedPriority.value) return;
      selectedPriority.value = null;
      showPriorityMenu.value = false;
      return;
    }

    const normalizedPriorityId = String(nextPriority?.id || '').trim();
    if (!normalizedPriorityId) return;

    const matchedPriority = priorityOptions.value.find(
      priority => priority.id === normalizedPriorityId
    ) || {
      id: normalizedPriorityId,
      label: nextPriority?.label || normalizedPriorityId,
    };

    if (selectedPriority.value?.id === matchedPriority.id) return;
    selectedPriority.value = matchedPriority;
    showPriorityMenu.value = false;
  },
  { immediate: true }
);

const onCustomDateChange = value => {
  if (!isValidCustomDateRange(value)) return;
  customDateRange.value = value;
  showDateMenu.value = false;
};
</script>

<template>
  <div class="cdf-row">
    <div class="cdf-left">
      <div
        v-if="props.showAddFilter"
        ref="addFilterMenuRef"
        class="cdf-filter-anchor"
      >
        <button
          class="cdf-secondary-btn"
          type="button"
          :aria-expanded="showAddFilterMenu ? 'true' : 'false'"
          @click.stop="toggleMenu('addFilter')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path
              stroke="currentcolor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 12h14m-7-7v14"
            />
          </svg>
          <span>{{ t('REPORT.COMPACT_FILTERS.ADD_FILTER') }}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m6 9 6 6 6-6"
            />
          </svg>
        </button>

        <div v-if="showAddFilterMenu" class="cdf-action-menu" @click.stop>
          <button
            class="cdf-action-menu__item"
            :class="{ 'is-selected': isFilterKeyEnabled('agents') }"
            type="button"
            :disabled="!props.showAgents"
            @click="onAddFilterItemClick('agents')"
          >
            <span class="cdf-action-menu__icon" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6.168 18.849A4 4 0 0 1 10 16h4a4 4 0 0 1 3.834 2.855M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12m6-2a3 3 0 1 0 6 0 3 3 0 0 0-6 0"
                />
              </svg>
            </span>
            <span class="cdf-action-menu__label">{{
              t('REPORT.COMPACT_FILTERS.AGENT')
            }}</span>
          </button>

          <button
            class="cdf-action-menu__item"
            :class="{ 'is-selected': isFilterKeyEnabled('inbox') }"
            type="button"
            :disabled="!props.showInbox"
            @click="onAddFilterItemClick('inbox')"
          >
            <span class="cdf-action-menu__icon" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 17h-5.397a5 5 0 0 1-4.096-2.133l-.514-.734A5 5 0 0 0 6.897 12H3h3.9a5 5 0 0 0 4.097-2.135l.51-.73A5 5 0 0 1 15.605 7H21m0 10-3 3m3-3-3-3m3-7-3 3m3-3-3-3"
                />
              </svg>
            </span>
            <span class="cdf-action-menu__label">{{
              t('REPORT.COMPACT_FILTERS.INBOX')
            }}</span>
          </button>

          <button
            class="cdf-action-menu__item"
            :class="{ 'is-selected': isFilterKeyEnabled('labels') }"
            type="button"
            :disabled="!props.showLabels"
            @click="onAddFilterItemClick('labels')"
          >
            <span class="cdf-action-menu__icon" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  fill="currentcolor"
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8.5 9.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2"
                />
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 7v3.859c0 .537.213 1.052.593 1.432l8.116 8.116a2.023 2.023 0 0 0 2.864 0l4.834-4.834a2.023 2.023 0 0 0 0-2.864L12.29 4.593A2.03 2.03 0 0 0 10.859 4H7a3 3 0 0 0-3 3"
                />
              </svg>
            </span>
            <span class="cdf-action-menu__label">{{
              t('REPORT.COMPACT_FILTERS.LABEL')
            }}</span>
          </button>

          <button
            class="cdf-action-menu__item"
            :class="{ 'is-selected': isFilterKeyEnabled('teams') }"
            type="button"
            :disabled="!props.showTeams"
            @click="onAddFilterItemClick('teams')"
          >
            <span class="cdf-action-menu__icon" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13.5 17a4 4 0 1 0 8 0 4 4 0 0 0-8 0M2.5 17a4 4 0 1 0 8 0 4 4 0 0 0-8 0M8 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0"
                />
              </svg>
            </span>
            <span class="cdf-action-menu__label">{{
              t('REPORT.COMPACT_FILTERS.TEAM')
            }}</span>
          </button>

          <button
            class="cdf-action-menu__item"
            :class="{ 'is-selected': isFilterKeyEnabled('vip') }"
            type="button"
            :disabled="!props.showVip"
            @click="onAddFilterItemClick('vip')"
          >
            <span class="cdf-action-menu__icon" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 3l2.475 5.316 5.525.738-4.045 3.87.988 5.487L12 15.9l-4.943 2.51.988-5.487L4 9.054l5.525-.738z"
                />
              </svg>
            </span>
            <span class="cdf-action-menu__label">{{
              t('REPORT.COMPACT_FILTERS.VIP')
            }}</span>
          </button>

          <button
            class="cdf-action-menu__item"
            :class="{ 'is-selected': isFilterKeyEnabled('issue') }"
            type="button"
            :disabled="!props.showIssue"
            @click="onAddFilterItemClick('issue')"
          >
            <span class="cdf-action-menu__icon" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 16h6M4 8h2m-2 4h2m-2 4h2m5-6a2 2 0 1 0 4 0 2 2 0 0 0-4 0m9-4v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2"
                />
              </svg>
            </span>
            <span class="cdf-action-menu__label">{{
              t('REPORT.COMPACT_FILTERS.ISSUE')
            }}</span>
          </button>

          <button
            class="cdf-action-menu__item"
            :class="{ 'is-selected': isFilterKeyEnabled('priority') }"
            type="button"
            :disabled="!props.showPriority"
            @click="onAddFilterItemClick('priority')"
          >
            <span class="cdf-action-menu__icon" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 3v10m0 0 4-4m-4 4-4-4M4 21h16"
                />
              </svg>
            </span>
            <span class="cdf-action-menu__label">{{
              t('REPORT.COMPACT_FILTERS.PRIORITY')
            }}</span>
          </button>
        </div>
      </div>

      <div
        v-if="props.showDateFilter"
        ref="dateMenuRef"
        class="cdf-chip-wrap"
        :class="{ 'cdf-chip-wrap--open': showDateMenu }"
      >
        <div class="css-ux9bug">
          <button
            class="css-kdte16"
            type="button"
            :aria-label="t('REPORT.COMPACT_FILTERS.PREVIOUS_PERIOD')"
            @click.stop="shiftDateRange('prev')"
          >
            <span class="lc-Icon-module__icon___J5RH5" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m15 19-7-7 7-7"
                />
              </svg>
            </span>
          </button>

          <div class="cdf-date-nav__middle">
            <button
              class="cdf-chip cdf-chip--date"
              type="button"
              :aria-expanded="showDateMenu ? 'true' : 'false'"
              @click.stop="toggleMenu('date')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 11h16M8 3v4m8-4v4m-8 8h2v2H8zM4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"
                />
              </svg>
              <strong>{{ t('REPORT.COMPACT_FILTERS.DATE') }}</strong>
              <span class="cdf-is">{{ t('REPORT.COMPACT_FILTERS.IS') }}</span>
              <span class="cdf-chip-value">{{ dateChipLabel }}</span>
            </button>
          </div>

          <button
            class="css-kdte16"
            type="button"
            :aria-label="t('REPORT.COMPACT_FILTERS.NEXT_PERIOD')"
            :disabled="!canShiftNext"
            @click.stop="shiftDateRange('next')"
          >
            <span class="lc-Icon-module__icon___J5RH5" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m9 5 7 7-7 7"
                />
              </svg>
            </span>
          </button>
        </div>

        <!-- Date picker anchor: always in DOM so popup can open at the right position -->
        <div class="cdf-picker-anchor">
          <WootDateRangePicker
            ref="customDatePickerRef"
            :value="customDateRange"
            :lang="datePickerLang"
            :disabled-date="disableFutureDates"
            :confirm-text="t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
            @change="onCustomDateChange"
          />
        </div>

        <!-- Date options dropdown: only shown when menu is open AND not in custom-picker mode -->
        <div v-if="showDateMenu" class="cdf-menu" @click.stop>
          <div class="cdf-menu__title">
            {{ t('REPORT.COMPACT_FILTERS.DATE_FILTER') }}
          </div>
          <button
            v-for="opt in dateOptions"
            :key="opt.id"
            class="cdf-menu__item"
            :class="{
              'cdf-menu__item--active': opt.id === selectedDateRange?.id,
            }"
            type="button"
            @click="onSelectDate(opt)"
          >
            <span>{{ opt.name }}</span>
            <span
              v-if="opt.id === selectedDateRange?.id"
              class="cdf-menu__check"
              aria-hidden="true"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="14"
                height="14"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m5 13 4 4L19 7"
                />
              </svg>
            </span>
          </button>
        </div>
      </div>

      <div
        v-if="props.showAgents && isFilterKeyVisible('agents')"
        ref="agentsMenuRef"
        class="cdf-chip-wrap"
        :class="{ 'cdf-chip-wrap--open': showAgentsMenu }"
      >
        <button
          class="cdf-chip"
          type="button"
          :aria-expanded="showAgentsMenu ? 'true' : 'false'"
          @click.stop="toggleMenu('agents')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path
              stroke="currentcolor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"
            />
          </svg>
          <strong>{{ t('REPORT.COMPACT_FILTERS.AGENTS') }}</strong>
          <span class="cdf-is">{{ t('REPORT.COMPACT_FILTERS.IS') }}</span>
          <span class="cdf-chip-value">{{ agentsChipLabel }}</span>
        </button>

        <div
          v-if="showAgentsMenu"
          class="cdf-menu cdf-menu--agents"
          @click.stop
        >
          <div class="cdf-menu__title">
            {{ t('REPORT.COMPACT_FILTERS.AGENTS_FILTER') }}
          </div>
          <button class="cdf-menu__item" type="button" @click="clearAgents">
            <span>{{ allAgentsLabel }}</span>
          </button>
          <button
            v-for="agent in agents"
            :key="agent.id"
            class="cdf-menu__item"
            :class="{
              'cdf-menu__item--active': selectedAgents.some(
                a => a.id === agent.id
              ),
            }"
            type="button"
            @click="toggleAgent(agent)"
          >
            <span class="cdf-truncate">{{ agent.name }}</span>
          </button>
        </div>
      </div>

      <div
        v-if="props.showLabels && isFilterKeyVisible('labels')"
        ref="labelsMenuRef"
        class="cdf-chip-wrap"
        :class="{ 'cdf-chip-wrap--open': showLabelsMenu }"
      >
        <button
          class="cdf-chip"
          type="button"
          :aria-expanded="showLabelsMenu ? 'true' : 'false'"
          @click.stop="toggleMenu('labels')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path
              stroke="currentcolor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M7 7h.01M3 11l8.586 8.586a2 2 0 0 0 2.828 0L21 13a2 2 0 0 0 0-2.828L12.414 1.586a2 2 0 0 0-2.828 0L3 8.172A2 2 0 0 0 3 11z"
            />
          </svg>
          <strong>{{ t('REPORT.COMPACT_FILTERS.LABEL') }}</strong>
          <span class="cdf-is">{{ t('REPORT.COMPACT_FILTERS.IS') }}</span>
          <span class="cdf-chip-value">{{ labelsChipLabel }}</span>
        </button>

        <div
          v-if="showLabelsMenu"
          class="cdf-menu cdf-menu--labels"
          @click.stop
        >
          <div class="cdf-menu__title">
            {{ t('REPORT.COMPACT_FILTERS.LABEL_FILTER') }}
          </div>
          <button class="cdf-menu__item" type="button" @click="clearLabel">
            <span>{{ allLabelsLabel }}</span>
          </button>
          <button
            v-for="label in labels"
            :key="label.id"
            class="cdf-menu__item"
            :class="{
              'cdf-menu__item--active': label.id === selectedLabel?.id,
            }"
            type="button"
            @click="onSelectLabel(label)"
          >
            <span class="cdf-truncate">{{ label.title }}</span>
          </button>
        </div>
      </div>

      <div
        v-if="props.showTeams && isFilterKeyVisible('teams')"
        ref="teamsMenuRef"
        class="cdf-chip-wrap"
        :class="{ 'cdf-chip-wrap--open': showTeamsMenu }"
      >
        <button
          class="cdf-chip"
          type="button"
          :aria-expanded="showTeamsMenu ? 'true' : 'false'"
          @click.stop="toggleMenu('teams')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path
              stroke="currentcolor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 21v-2a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v2M7 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0"
            />
          </svg>
          <strong>{{ t('REPORT.COMPACT_FILTERS.TEAM') }}</strong>
          <span class="cdf-is">{{ t('REPORT.COMPACT_FILTERS.IS') }}</span>
          <span class="cdf-chip-value">{{ teamsChipLabel }}</span>
        </button>

        <div v-if="showTeamsMenu" class="cdf-menu cdf-menu--teams" @click.stop>
          <div class="cdf-menu__title">
            {{ t('REPORT.COMPACT_FILTERS.TEAM_FILTER') }}
          </div>
          <button class="cdf-menu__item" type="button" @click="onClearTeam">
            <span>{{ allTeamsLabel }}</span>
          </button>
          <button
            v-for="team in teams"
            :key="team.id"
            class="cdf-menu__item"
            :class="{ 'cdf-menu__item--active': team.id === selectedTeam?.id }"
            type="button"
            @click="onSelectTeam(team)"
          >
            <span class="cdf-truncate">{{ team.name }}</span>
          </button>
        </div>
      </div>

      <div
        v-if="props.showVip && isFilterKeyVisible('vip')"
        ref="vipMenuRef"
        class="cdf-chip-wrap"
        :class="{ 'cdf-chip-wrap--open': showVipMenu }"
      >
        <button
          class="cdf-chip"
          type="button"
          :aria-expanded="showVipMenu ? 'true' : 'false'"
          @click.stop="toggleMenu('vip')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path
              stroke="currentcolor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 3l2.475 5.316 5.525.738-4.045 3.87.988 5.487L12 15.9l-4.943 2.51.988-5.487L4 9.054l5.525-.738z"
            />
          </svg>
          <strong>{{ t('REPORT.COMPACT_FILTERS.VIP') }}</strong>
          <span class="cdf-is">{{ t('REPORT.COMPACT_FILTERS.IS') }}</span>
          <span class="cdf-chip-value">{{ vipChipLabel }}</span>
        </button>

        <div v-if="showVipMenu" class="cdf-menu cdf-menu--vip" @click.stop>
          <div class="cdf-menu__title">
            {{ t('REPORT.COMPACT_FILTERS.VIP_FILTER') }}
          </div>
          <button class="cdf-menu__item" type="button" @click="onClearVip">
            <span>{{ allVipLabel }}</span>
          </button>
          <button
            v-for="vipLevel in vipLevels"
            :key="vipLevel.id"
            class="cdf-menu__item"
            :class="{
              'cdf-menu__item--active': vipLevel.id === selectedVip?.id,
            }"
            type="button"
            @click="onSelectVip(vipLevel)"
          >
            <span class="cdf-truncate">{{ vipOptionLabel(vipLevel) }}</span>
          </button>
        </div>
      </div>

      <div
        v-if="props.showIssue && isFilterKeyVisible('issue')"
        ref="issueMenuRef"
        class="cdf-chip-wrap"
        :class="{ 'cdf-chip-wrap--open': showIssueMenu }"
      >
        <button
          class="cdf-chip"
          type="button"
          :aria-expanded="showIssueMenu ? 'true' : 'false'"
          @click.stop="toggleMenu('issue')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path
              stroke="currentcolor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 16h6M4 8h2m-2 4h2m-2 4h2m5-6a2 2 0 1 0 4 0 2 2 0 0 0-4 0m9-4v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2"
            />
          </svg>
          <strong>{{ t('REPORT.COMPACT_FILTERS.ISSUE') }}</strong>
          <span class="cdf-is">{{ t('REPORT.COMPACT_FILTERS.IS') }}</span>
          <span class="cdf-chip-value">{{ issueChipLabel }}</span>
        </button>

        <div v-if="showIssueMenu" class="cdf-menu cdf-menu--issue" @click.stop>
          <div class="cdf-menu__title">
            {{ t('REPORT.COMPACT_FILTERS.ISSUE_FILTER') }}
          </div>
          <button class="cdf-menu__item" type="button" @click="onClearIssue">
            <span>{{ allIssueLabel }}</span>
          </button>
          <button
            v-for="issue in issues"
            :key="issue.id"
            class="cdf-menu__item"
            :class="{
              'cdf-menu__item--active': issue.id === selectedIssue?.id,
            }"
            type="button"
            @click="onSelectIssue(issue)"
          >
            <span class="cdf-truncate">{{ issue.name }}</span>
          </button>
        </div>
      </div>

      <div
        v-if="props.showPriority && isFilterKeyVisible('priority')"
        ref="priorityMenuRef"
        class="cdf-chip-wrap"
        :class="{ 'cdf-chip-wrap--open': showPriorityMenu }"
      >
        <button
          class="cdf-chip"
          type="button"
          :aria-expanded="showPriorityMenu ? 'true' : 'false'"
          @click.stop="toggleMenu('priority')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path
              stroke="currentcolor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 3v10m0 0 4-4m-4 4-4-4M4 21h16"
            />
          </svg>
          <strong>{{ t('REPORT.COMPACT_FILTERS.PRIORITY') }}</strong>
          <span class="cdf-is">{{ t('REPORT.COMPACT_FILTERS.IS') }}</span>
          <span class="cdf-chip-value">{{ priorityChipLabel }}</span>
        </button>

        <div
          v-if="showPriorityMenu"
          class="cdf-menu cdf-menu--priority"
          @click.stop
        >
          <div class="cdf-menu__title">
            {{ t('REPORT.COMPACT_FILTERS.PRIORITY_FILTER') }}
          </div>
          <button class="cdf-menu__item" type="button" @click="onClearPriority">
            <span>{{ allPriorityLabel }}</span>
          </button>
          <button
            v-for="priority in priorityOptions"
            :key="priority.id"
            class="cdf-menu__item"
            :class="{
              'cdf-menu__item--active': priority.id === selectedPriority?.id,
            }"
            type="button"
            @click="onSelectPriority(priority)"
          >
            <span class="cdf-truncate">{{ priority.label }}</span>
          </button>
        </div>
      </div>

      <div
        v-if="props.showInbox && isFilterKeyVisible('inbox')"
        ref="inboxMenuRef"
        class="cdf-chip-wrap"
        :class="{ 'cdf-chip-wrap--open': showInboxMenu }"
      >
        <button
          class="cdf-chip"
          type="button"
          :aria-expanded="showInboxMenu ? 'true' : 'false'"
          @click.stop="toggleMenu('inbox')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path
              stroke="currentcolor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4h16v16H4z"
            />
          </svg>
          <strong>{{ t('REPORT.COMPACT_FILTERS.INBOX') }}</strong>
          <span class="cdf-is">{{ t('REPORT.COMPACT_FILTERS.IS') }}</span>
          <span class="cdf-chip-value">{{ inboxChipLabel }}</span>
        </button>

        <div v-if="showInboxMenu" class="cdf-menu cdf-menu--inbox" @click.stop>
          <div class="cdf-menu__title">
            {{ t('REPORT.COMPACT_FILTERS.INBOX_FILTER') }}
          </div>
          <button class="cdf-menu__item" type="button" @click="onClearInbox">
            <span>{{ allInboxesLabel }}</span>
            <span
              v-if="props.multiInboxMode && !selectedInboxes.length"
              class="cdf-menu__check"
              aria-hidden="true"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="14"
                height="14"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m5 13 4 4L19 7"
                />
              </svg>
            </span>
          </button>
          <button
            v-for="inbox in inboxes"
            :key="inbox.id"
            class="cdf-menu__item"
            :class="{
              'cdf-menu__item--active': props.multiInboxMode
                ? selectedInboxes.some(i => i.id === inbox.id)
                : inbox.id === selectedInbox?.id,
            }"
            type="button"
            @click="
              props.multiInboxMode ? toggleInbox(inbox) : onSelectInbox(inbox)
            "
          >
            <span class="cdf-truncate">{{ inbox.name }}</span>
            <span
              v-if="
                props.multiInboxMode &&
                selectedInboxes.some(i => i.id === inbox.id)
              "
              class="cdf-menu__check"
              aria-hidden="true"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="14"
                height="14"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m5 13 4 4L19 7"
                />
              </svg>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cdf-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.cdf-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.cdf-secondary-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #ffffff;
  color: #111827;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.cdf-filter-anchor {
  position: relative;
}

.cdf-action-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 240px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.12) 0 14px 38px;
  padding: 6px;
  z-index: 60;
}

.cdf-action-menu__item {
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border-radius: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 10px;
}

.cdf-action-menu__item:hover {
  background: #f3f4f6;
}

.cdf-action-menu__item.is-selected {
  background: #eef2ff;
  color: #1d4ed8;
}

.cdf-action-menu__item:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.cdf-action-menu__item:disabled:hover {
  background: transparent;
}

.cdf-action-menu__icon {
  display: inline-flex;
  align-items: center;
  color: currentcolor;
}

.cdf-action-menu__label {
  flex: 1 1 auto;
  min-width: 0;
}

.css-ux9bug {
  display: flex;
  align-items: center;
  height: 34px;
  background-color: #ffffff;
  border: 1px solid #d9dde3;
  border-radius: 8px;
  padding: 0;
}

.css-ux9bug > button:first-of-type {
  margin-right: 0;
}

.css-ux9bug > button:last-of-type {
  margin-left: 0;
}

.css-kdte16 {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #111827;
  cursor: pointer;
}

.css-kdte16:hover {
  background: rgba(17, 24, 39, 0.08);
}

.css-kdte16:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.cdf-date-nav__middle {
  padding: 0;
}

.cdf-chip-wrap {
  position: relative;
}

.cdf-chip-wrap--open {
  z-index: 40;
}

.cdf-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #f3f4f6;
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.cdf-chip--date {
  height: 32px;
  border: 0;
  background: transparent;
  border-radius: 6px;
  padding: 0 8px;
}

.cdf-chip-value {
  font-weight: 600;
  color: #4b5563;
}

.cdf-is {
  font-weight: 600;
  color: #111827;
}

.cdf-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 240px;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 20px 28px rgba(17, 24, 39, 0.14);
  z-index: 50;
}

/* Hidden anchor that keeps WootDateRangePicker in the DOM so its popup can open */
.cdf-picker-anchor {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 0;
  height: 0;
  overflow: visible;
  pointer-events: none;
}

.cdf-picker-anchor :deep(.mx-datepicker) {
  position: absolute;
  width: 1px !important;
  height: 1px !important;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

/* The popup itself is appended to body by vue2-datepicker, so it is unaffected by the above */

.cdf-menu--inbox {
  width: 280px;
  max-height: 360px;
  overflow: auto;
}

.cdf-menu--agents {
  width: 320px;
  max-height: 360px;
  overflow: auto;
}

.cdf-menu--labels {
  width: 280px;
  max-height: 360px;
  overflow: auto;
}

.cdf-menu--teams {
  width: 280px;
  max-height: 360px;
  overflow: auto;
}

.cdf-menu--vip {
  width: 280px;
  max-height: 360px;
  overflow: auto;
}

.cdf-menu--issue {
  width: 280px;
  max-height: 360px;
  overflow: auto;
}

.cdf-menu--priority {
  width: 240px;
  max-height: 320px;
  overflow: auto;
}

.cdf-menu__title {
  margin: 0 0 6px;
  padding: 0 4px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.cdf-menu__item {
  width: 100%;
  min-height: 34px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.cdf-menu__item:hover {
  background: #f3f4f6;
}

.cdf-menu__item--active {
  background: #eef2ff;
  color: #1d4ed8;
  font-weight: 600;
}

.cdf-menu__check {
  display: inline-flex;
  align-items: center;
  color: currentcolor;
}

.cdf-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
